import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import CirurgiaDetailsModal from "../../components/Modals/CirurgiaDetailsModal/index.tsx";
import NovaCirurgiaForm from "../../components/CadastroCirurgia/NovaCirurgiaForm.tsx";
import Drawer from "../../ui/Drawer.tsx";
import handleService from "../../services/apiService.ts";
import ConfirmDeleteDialog from "../../components/Modals/ConfirmDeleteDialog/index.tsx";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import { usePermissoes } from "../../contexts/PermissoesContext.tsx";
import { MethodsEnum } from "../../enums/Methods.ts";
import { useFeedback } from "../../contexts/FeedbackContext.tsx";
import { emitirNotificacao } from "../../contexts/NotificationContext.tsx";
import { STATUS_LABEL, tipoNotificacaoDoStatus } from "../../utils/statusCirurgia.util.ts";
import { formatarDataCirurgia } from "../../utils/relativeTime.util.ts";
import Button from "../../ui/Button.tsx";
import Pill from "../../ui/Pill.tsx";
import Modal from "../../ui/Modal.tsx";
import EmptyState from "../../ui/EmptyState.tsx";
import StatTile from "../../ui/StatTile.tsx";
import DropdownMenu from "../../ui/DropdownMenu.tsx";
import StatusBadge, { statusColor } from "../../ui/StatusBadge.tsx";
import "../../styles/design-system.css";

interface Cirurgia {
  id: number;
  data: string;
  tipoCirurgia: string;
  medico: string;
  paciente: string;
  instrumentador1: string;
  instrumentador2: string;
  instrumentador3: string;
  convenio: string;
  hospital: string;
  observacao: string;
  status: "AGENDADA" | "REALIZADA" | "SEPARADO" | "CANCELADA" | "ABERTO";
  motivo?: string;
}

const STATUS_FILTERS = [
  { value: "TODOS", label: "Todas" },
  { value: "AGENDADA", label: "Agendada" },
  { value: "SEPARADO", label: "Separado" },
  { value: "REALIZADA", label: "Realizada" },
  { value: "CANCELADA", label: "Cancelada" },
  { value: "ABERTO", label: "Aberto" },
];

const getDefaultDates = () => {
  const fmt = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const today = new Date();
  return { dataInicial: fmt(today), dataFinal: fmt(today) };
};

const hourOf = (iso: string) =>
  new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
const dateShort = (iso: string) =>
  new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
const dayKey = (iso: string) => iso.slice(0, 10);
const dayHeading = (key: string) => {
  const today = getDefaultDates().dataInicial;
  const d = new Date(`${key}T00:00:00`);
  const label = d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
  return key === today ? `Hoje — ${label}` : label.charAt(0).toUpperCase() + label.slice(1);
};
const dateLabel = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });

const STATUS_ACTIONS: { status: Cirurgia["status"]; label: string; icon: React.ReactNode }[] = [
  { status: "AGENDADA", label: "Marcar como Agendada", icon: <UndoOutlinedIcon fontSize="small" /> },
  { status: "SEPARADO", label: "Marcar como Separado", icon: <CheckCircleOutlineIcon fontSize="small" /> },
  { status: "REALIZADA", label: "Marcar como Realizada", icon: <DoneAllOutlinedIcon fontSize="small" /> },
  { status: "ABERTO", label: "Marcar como Aberto", icon: <ScheduleOutlinedIcon fontSize="small" /> },
];

const ListaCirurgias = () => {
  const { showFeedback } = useFeedback();
  const [novaCirurgiaAberta, setNovaCirurgiaAberta] = useState(false);
  const [novaCirurgiaEnviando, setNovaCirurgiaEnviando] = useState(false);
  const [cirurgias, setCirurgias] = useState<Cirurgia[]>([]);
  const [statusFiltro, setStatusFiltro] = useState<string>("TODOS");
  const [dataInicial, setDataInicial] = useState(getDefaultDates().dataInicial);
  const [dataFinal, setDataFinal] = useState(getDefaultDates().dataFinal);
  // Rascunho local dos campos de data: só vira busca de verdade ao clicar "Buscar" (ou Enter).
  // Antes, digitar a data disparava fetchCirurgias a cada onChange do input nativo, recarregando
  // a lista várias vezes durante a digitação — daqui pra frente dataInicial/dataFinal (que alimentam
  // o useEffect de busca) só mudam nesse momento explícito.
  const [dataInicialInput, setDataInicialInput] = useState(getDefaultDates().dataInicial);
  const [dataFinalInput, setDataFinalInput] = useState(getDefaultDates().dataFinal);
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  // Mais recentes primeiro por padrão (antes só existia ordem crescente fixa,
  // que num período longo mostrava a cirurgia mais antiga primeiro em vez da
  // mais relevante pra quem está olhando "agora").
  const [ordem, setOrdem] = useState<"asc" | "desc">("desc");
  const [detalheAberto, setDetalheAberto] = useState(false);
  const [cirurgiaSelecionada, setCirurgiaSelecionada] = useState<Cirurgia | null>(null);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [cirurgiaId, setCirurgiaId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalReadOnly, setIsModalReadOnly] = useState(false);
  const [actionLoading, setActionLoading] = useState<{ id: number; status: string } | null>(null);
  const [cirurgiaParaAbrir, setCirurgiaParaAbrir] = useState<number | null>(null);
  const { podeExcluirAgendamento, tipo, erroAoCarregar } = usePermissoes();

  // Espelha o PermissaoCatalogo do servidor: criar/editar e cancelar sao de ADM
  // e OPERADOR; o INSTRUMENTADOR ve a agenda e avanca status, so isso.
  //
  // Sem estas duas guardas a tela oferecia "Nova cirurgia", o lapis de editar e
  // "Cancelar cirurgia" para um instrumentador - ele clicava, preenchia, e o
  // servidor devolvia 403 no fim. Nao era brecha (quem autoriza e o
  // FuncionalidadeInterceptor), era a interface prometendo o que a API nega.
  //
  // erroAoCarregar libera de proposito: nao foi possivel confirmar a funcao, e
  // esconder acao de quem tem direito e pior do que mostrar e receber 403 com
  // mensagem clara - mesma regra do resto do app.
  const podeCriarOuEditar = erroAoCarregar || tipo === "ADM" || tipo === "OPERADOR";
  const podeCancelar = podeCriarOuEditar;
  const [cirurgiaParaExcluir, setCirurgiaParaExcluir] = useState<Cirurgia | null>(null);
  const [excluindo, setExcluindo] = useState(false);

  const hasValidDates = dataInicial && dataFinal;

  const fetchCirurgias = useCallback(async () => {
    if (!hasValidDates) {
      setCirurgias([]);
      setTotalPaginas(0);
      setIsLoading(false);
      return;
    }
    const params = new URLSearchParams({ page: String(paginaAtual), size: "50", ordem });
    if (statusFiltro && statusFiltro !== "TODOS") params.append("status", statusFiltro);
    params.append("dataInicial", dataInicial);
    params.append("dataFinal", dataFinal);

    try {
      setIsLoading(true);
      const response = await handleService({ method: MethodsEnum.GET, endpoint: `/cirurgia?${params.toString()}` });
      setCirurgias(response.data.content);
      setTotalPaginas(response.data.totalPages);
    } catch (err) {
      setCirurgias([]);
    } finally {
      setIsLoading(false);
    }
  }, [paginaAtual, statusFiltro, dataInicial, dataFinal, hasValidDates, ordem]);

  useEffect(() => {
    fetchCirurgias();
  }, [fetchCirurgias]);

  // Contagens por status do período/filtro de data atual, independente do filtro de
  // status selecionado — busca "TODOS" sempre que dataInicial/dataFinal mudam, só pra
  // alimentar os stat cards e os números nos pills (não interfere na lista principal).
  // Extraída como função reutilizável (não só efeito) porque também precisa ser
  // reexecutada depois de qualquer mutação de cirurgia (troca de status, cancelamento,
  // nova cirurgia) — antes disso os cards só recalculavam ao trocar o período, exigindo
  // F5 pra refletir uma mudança de status feita na própria tela.
  const [contagens, setContagens] = useState<Record<string, number>>({});
  const fetchContagens = useCallback(async () => {
    if (!hasValidDates) return;
    try {
      const params = new URLSearchParams({ dataInicial, dataFinal });
      const r = await handleService({ method: MethodsEnum.GET, endpoint: `/cirurgia/contagem?${params.toString()}` });
      setContagens(r.data || {});
    } catch {
      setContagens({});
    }
  }, [dataInicial, dataFinal, hasValidDates]);

  useEffect(() => {
    fetchContagens();
  }, [fetchContagens]);

  const aplicarPeriodo = () => {
    setDataInicial(dataInicialInput);
    setDataFinal(dataFinalInput);
    setPaginaAtual(0);
  };

  const alternarOrdem = () => {
    setOrdem((atual) => (atual === "desc" ? "asc" : "desc"));
    setPaginaAtual(0);
  };

  const handleDateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") aplicarPeriodo();
  };

  const alterStatus = async (id: number, status: string, motivoTexto = "") => {
    setActionLoading({ id, status });
    const url = status === "CANCELADA" ? "cancelar" : "status";
    const body = status === "CANCELADA" ? { id, motivo: motivoTexto } : { id, status };
    try {
      const response = await handleService({
        method: MethodsEnum[status === "CANCELADA" ? "DELETE" : "PATCH"],
        endpoint: `/cirurgia/${url}`,
        data: body,
      });
      const cirurgia = response?.data;
      if (cirurgia?.id) {
        const tipo = tipoNotificacaoDoStatus(status as Cirurgia["status"]);
        const descricao =
          status === "CANCELADA"
            ? `A cirurgia de ${cirurgia.paciente}, marcada para ${formatarDataCirurgia(cirurgia.data)}, foi cancelada${motivoTexto ? ` — ${motivoTexto}` : "."}`
            : status === "REALIZADA"
            ? `A cirurgia de ${cirurgia.paciente} (${formatarDataCirurgia(cirurgia.data)}) foi marcada como realizada.`
            : status === "AGENDADA"
            ? `Cirurgia de ${cirurgia.paciente} marcada para ${formatarDataCirurgia(cirurgia.data)} — ${cirurgia.hospital}.`
            : `A cirurgia de ${cirurgia.paciente} (${formatarDataCirurgia(cirurgia.data)}) foi atualizada para "${STATUS_LABEL[status as Cirurgia["status"]]}".`;
        emitirNotificacao({
          id: `cirurgia-${cirurgia.id}-${status.toLowerCase()}-${Date.now()}`,
          tipo,
          titulo: status === "CANCELADA" ? "Cirurgia cancelada" : status === "REALIZADA" ? "Cirurgia concluída" : status === "AGENDADA" ? "Cirurgia agendada" : "Status atualizado",
          descricao,
          link: "/cirurgias",
        });
      }
      fetchCirurgias();
      fetchContagens();
    } catch (err) {
      // erro já reportado globalmente pelo interceptor do apiService
    } finally {
      setActionLoading(null);
    }
  };

  const handleSalvarEdicao = async (dados: any) => {
    const payload = {
      id: dados.id,
      data: dados.data,
      medico: dados.medico || null,
      paciente: dados.paciente || null,
      instrumentador1: dados.instrumentador1 || null,
      instrumentador2: dados.instrumentador2 || null,
      instrumentador3: dados.instrumentador3 || null,
      hospital: dados.hospital || null,
      convenio: dados.convenio || null,
      tipo: dados.tipoCirurgia,
      observacao: dados.observacao || null,
      observacaoTipoCirurgia: dados.observacaoTipoCirurgia || null,
    };
    try {
      await handleService({ method: MethodsEnum.PATCH, endpoint: "/cirurgia/alterar", data: payload });
      fetchCirurgias();
    } catch (error: any) {
      throw error;
    }
  };

  const openCancelModal = (c: Cirurgia) => {
    setCirurgiaId(c.id);
    if (c.status === "CANCELADA") {
      setMotivo(c.motivo || "");
      setIsModalReadOnly(true);
    } else {
      setMotivo("");
      setIsModalReadOnly(false);
    }
    setOpenModal(true);
  };

  const confirmCancel = async () => {
    if (isModalReadOnly) {
      setOpenModal(false);
      return;
    }
    if (!motivo.trim() || cirurgiaId === null) return showFeedback("Motivo obrigatório!", "error");
    await alterStatus(cirurgiaId, "CANCELADA", motivo);
    setOpenModal(false);
    setMotivo("");
  };

  const confirmMarcarAberto = async () => {
    if (cirurgiaParaAbrir === null) return;
    await alterStatus(cirurgiaParaAbrir, "ABERTO");
    setCirurgiaParaAbrir(null);
  };

  const abrirDetalhes = (c: Cirurgia) => {
    setCirurgiaSelecionada(c);
    setDetalheAberto(true);
  };

  const buscarCirurgiaParaEdicao = async (id: number) => {
    setActionLoading({ id, status: "carregando" });
    try {
      const response = await handleService({ method: MethodsEnum.GET, endpoint: `/cirurgia/${id}` });
      if (!response.data?.id) {
        showFeedback("Erro: ID da cirurgia não encontrado", "error");
        return;
      }
      setCirurgiaSelecionada(response.data);
      setModoEdicao(true);
      setDetalheAberto(true);
    } catch (err) {
      showFeedback("Erro ao buscar dados da cirurgia", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const exportarCSV = () => {
    const linhas = ["Data,Hora,Paciente,Medico,Hospital,Convenio,Tipo,Status"];
    cirurgias.forEach((c) => {
      linhas.push(
        [dateShort(c.data), hourOf(c.data), c.paciente, c.medico, c.hospital, c.convenio || "", c.tipoCirurgia, c.status]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(",")
      );
    });
    const blob = new Blob([linhas.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `cirurgias_${dataInicial}_a_${dataFinal}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const grupos = useMemo(() => {
    const sinal = ordem === "asc" ? 1 : -1;
    const map = new Map<string, Cirurgia[]>();
    [...cirurgias]
      .sort((a, b) => sinal * (new Date(a.data).getTime() - new Date(b.data).getTime()))
      .forEach((c) => {
        const key = dayKey(c.data);
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(c);
      });
    return Array.from(map.entries()).sort(([a], [b]) => sinal * a.localeCompare(b));
  }, [cirurgias, ordem]);

  const hojeKey = getDefaultDates().dataInicial;
  const agoraMinutos = new Date().getHours() * 60 + new Date().getMinutes();

  const confirmarExclusao = async () => {
    if (!cirurgiaParaExcluir) return;
    try {
      setExcluindo(true);
      await handleService({
        method: MethodsEnum.DELETE,
        endpoint: `/cirurgia/${cirurgiaParaExcluir.id}`,
      });
      showFeedback("Cirurgia excluída definitivamente.", "success");
      setCirurgiaParaExcluir(null);
      // As duas: a linha some da lista e os contadores por status mudam junto.
      fetchCirurgias();
      fetchContagens();
    } catch (err: any) {
      showFeedback(err.message || "Erro ao excluir a cirurgia", "error");
    } finally {
      setExcluindo(false);
    }
  };

  const renderMenuItems = (c: Cirurgia) => {
    const items = STATUS_ACTIONS.filter((a) => a.status !== c.status).map((a) => ({
      label: a.label,
      icon: a.icon,
      onClick: () => (a.status === "ABERTO" ? setCirurgiaParaAbrir(c.id) : alterStatus(c.id, a.status)),
    }));

    // Só aparece para quem tem a permissão individual ligada. Fica por último
    // e separado do cancelamento de propósito: são coisas diferentes, e esta
    // não tem volta.
    const excluir = podeExcluirAgendamento
      ? [{
          label: "Excluir definitivamente",
          icon: <DeleteForeverOutlinedIcon fontSize="small" />,
          onClick: () => setCirurgiaParaExcluir(c),
          danger: true,
        }]
      : [];

    if (c.status === "CANCELADA") {
      return [
        { label: "Ver motivo do cancelamento", icon: <VisibilityOutlinedIcon fontSize="small" />, onClick: () => openCancelModal(c) },
        ...items,
        ...excluir,
      ];
    }
    return [
      ...items,
      ...(podeCancelar
        ? [{ label: "Cancelar cirurgia", icon: <ClearOutlinedIcon fontSize="small" />, onClick: () => openCancelModal(c), danger: true }]
        : []),
      ...excluir,
    ];
  };

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Cirurgias</h1>
          <p>Acompanhe, filtre e gerencie todos os procedimentos agendados na rede.</p>
        </div>
        <div className="ds-page-head-actions">
          {podeCriarOuEditar && (
            <Button variant="primary" icon={<AddCircleOutlineIcon fontSize="small" />} onClick={() => setNovaCirurgiaAberta(true)}>
              Nova cirurgia
            </Button>
          )}
        </div>
      </div>

      <div className="ds-stat-grid">
        <StatTile
          label="Total no período"
          value={contagens.TODOS ?? "—"}
          icon={<EventNoteOutlinedIcon fontSize="small" />}
          color="navy"
          foot={`${dateLabel(dataInicial)} – ${dateLabel(dataFinal)}`}
        />
        <StatTile
          label="Agendadas"
          value={contagens.AGENDADA ?? "—"}
          icon={<CheckCircleOutlineIcon fontSize="small" />}
          color="blue"
          foot="Prontas para execução"
        />
        <StatTile
          label="Separadas"
          value={contagens.SEPARADO ?? "—"}
          icon={<ScheduleOutlinedIcon fontSize="small" />}
          color="orange"
          foot="Materiais separados"
        />
        <StatTile
          label="Canceladas"
          value={contagens.CANCELADA ?? "—"}
          icon={<ClearOutlinedIcon fontSize="small" />}
          color="red"
          foot="Verificar motivo"
        />
      </div>

      <div className="ds-toolbar">
        <div className="ds-status-pills">
          {STATUS_FILTERS.map((f) => (
            <Pill
              key={f.value}
              label={f.label}
              count={contagens[f.value]}
              active={statusFiltro === f.value}
              onClick={() => { setStatusFiltro(f.value); setPaginaAtual(0); }}
            />
          ))}
        </div>
        <div className="ds-toolbar-filters">
          <div className="ds-field">
            <label>Data inicial</label>
            <input
              type="date"
              value={dataInicialInput}
              onChange={(e) => setDataInicialInput(e.target.value)}
              onKeyDown={handleDateKeyDown}
            />
          </div>
          <div className="ds-field">
            <label>Data final</label>
            <input
              type="date"
              value={dataFinalInput}
              min={dataInicialInput}
              onChange={(e) => setDataFinalInput(e.target.value)}
              onKeyDown={handleDateKeyDown}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<SearchOutlinedIcon fontSize="small" />}
            onClick={aplicarPeriodo}
            disabled={dataInicialInput === dataInicial && dataFinalInput === dataFinal}
          >
            Buscar
          </Button>
          <Tooltip title={ordem === "desc" ? "Mostrando mais recentes primeiro — clique para inverter" : "Mostrando mais antigas primeiro — clique para inverter"}>
            <Button variant="outline" size="sm" icon={<SwapVertIcon fontSize="small" />} onClick={alternarOrdem} aria-label="Alternar ordem" />
          </Tooltip>
          <Button variant="outline" size="sm" icon={<DownloadOutlinedIcon fontSize="small" />} onClick={exportarCSV} disabled={cirurgias.length === 0}>
            Exportar
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="ds-card" style={{ padding: 40, textAlign: "center", color: "var(--ink-faint)" }}>Carregando...</div>
      )}

      {!isLoading && grupos.length === 0 && (
        <EmptyState
          title="Nenhuma cirurgia neste período"
          description="Ajuste os filtros de data/status ou cadastre uma nova cirurgia."
          actionLabel={podeCriarOuEditar ? "Nova cirurgia" : undefined}
          onAction={podeCriarOuEditar ? () => setNovaCirurgiaAberta(true) : undefined}
        />
      )}

      {!isLoading && grupos.map(([key, itens]) => {
        let inseridoAgora = key !== hojeKey;
        return (
          <div className="ds-tl-day" key={key}>
            <div className="ds-tl-day-head">
              <h3>{dayHeading(key)}</h3>
              <span className="count">{itens.length} procedimento{itens.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="ds-tl-list">
              {itens.map((c) => {
                const [hh, mm] = hourOf(c.data).split(":").map(Number);
                const minutos = hh * 60 + mm;
                const marcarAgora = !inseridoAgora && key === hojeKey && minutos >= agoraMinutos;
                if (marcarAgora) inseridoAgora = true;
                return (
                  <React.Fragment key={c.id}>
                    {marcarAgora && (
                      <div className="ds-tl-now">
                        <span>Agora</span>
                      </div>
                    )}
                    <div
                      className="ds-tl-row"
                      style={{ "--dotcolor": `var(--${statusColor(c.status) === "neutral" ? "ink-faint" : statusColor(c.status)})` } as React.CSSProperties}
                      onClick={() => abrirDetalhes(c)}
                    >
                      <div className="ds-tl-dot" />
                      <div className="ds-tl-row-time">
                        <span className="hour">{hourOf(c.data)}</span>
                        <span className="date">{dateShort(c.data)}</span>
                      </div>

                      <div className="ds-tl-row-title">
                        <span className="name">{c.paciente || "Paciente não informado"}</span>
                        <span className="proc">{c.tipoCirurgia}</span>
                      </div>

                      <div className="ds-tl-row-tags">
                        <span className="ds-tl-chip"><MedicalServicesOutlinedIcon />{c.medico || "—"}</span>
                        <span className="ds-tl-chip"><LocalHospitalOutlinedIcon />{c.hospital || "—"}</span>
                        {c.convenio && <span className="ds-tl-chip"><HealthAndSafetyOutlinedIcon />{c.convenio}</span>}
                      </div>

                      {(c.instrumentador1 || c.instrumentador2 || c.instrumentador3) && (
                        <div className="ds-tl-row-team">
                          <div className="ds-tl-team-label">Equipe</div>
                          {[c.instrumentador1, c.instrumentador2, c.instrumentador3].filter(Boolean).map((nome, i) => (
                            <div className="ds-tl-team-name" key={i}><BadgeOutlinedIcon />{nome}</div>
                          ))}
                        </div>
                      )}

                      <div className="ds-tl-row-right">
                        <StatusBadge status={c.status} />
                        <div className="ds-tl-row-actions" onClick={(e) => e.stopPropagation()}>
                          <button className="ds-icon-btn" onClick={() => abrirDetalhes(c)} title="Ver detalhes">
                            <VisibilityOutlinedIcon fontSize="small" />
                          </button>
                          {podeCriarOuEditar && (
                            <button
                              className="ds-icon-btn"
                              onClick={() => buscarCirurgiaParaEdicao(c.id)}
                              disabled={actionLoading?.id === c.id}
                              title="Editar"
                            >
                              {actionLoading?.id === c.id ? <CircularProgress size={16} /> : <EditOutlinedIcon fontSize="small" />}
                            </button>
                          )}
                          <DropdownMenu trigger={<MoreHorizIcon fontSize="small" />} items={renderMenuItems(c)} />
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })}

      {totalPaginas > 1 && (
        <div className="ds-pagination" style={{ justifyContent: "center", gap: 16 }}>
          <button className="ds-page-btn" disabled={paginaAtual === 0} onClick={() => setPaginaAtual((p) => p - 1)}>
            <ChevronLeftIcon fontSize="small" />
          </button>
          <span>Página {paginaAtual + 1} de {totalPaginas}</span>
          <button className="ds-page-btn" disabled={paginaAtual + 1 >= totalPaginas} onClick={() => setPaginaAtual((p) => p + 1)}>
            <ChevronRightIcon fontSize="small" />
          </button>
        </div>
      )}

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title="Motivo do cancelamento"
        icon={<WarningAmberOutlinedIcon fontSize="small" />}
        tone="danger"
        actions={
          isModalReadOnly ? (
            <Button variant="primary" onClick={() => setOpenModal(false)}>Fechar</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setOpenModal(false)}>Cancelar</Button>
              <Button
                variant="danger"
                onClick={confirmCancel}
                // Motivo vazio agora desabilita o botao em vez de deixar clicar
                // e so avisar por toast depois. O backend tambem passou a exigir
                // o motivo (@NotBlank + @Valid em CirurgiaController.cancelar).
                disabled={actionLoading?.status === "CANCELADA" || !motivo.trim()}
              >
                {actionLoading?.status === "CANCELADA" ? "Cancelando..." : "Confirmar cancelamento"}
              </Button>
            </>
          )
        }
      >
        <p>
          {isModalReadOnly
            ? "Este procedimento foi cancelado com o motivo abaixo."
            : "Informe o motivo do cancelamento para registro no histórico da cirurgia."}
        </p>
        <div className="ds-field">
          <label>Motivo do cancelamento</label>
          <textarea
            className="ds-textarea danger"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            readOnly={isModalReadOnly}
            rows={5}
            placeholder="Ex.: paciente solicitou remarcação, indisponibilidade de sala, intercorrência médica..."
          />
        </div>
      </Modal>

      <Modal
        open={cirurgiaParaAbrir !== null}
        onClose={() => setCirurgiaParaAbrir(null)}
        title="Marcar como Aberto"
        icon={<ScheduleOutlinedIcon fontSize="small" />}
        tone="danger"
        actions={
          <>
            <Button variant="outline" onClick={() => setCirurgiaParaAbrir(null)}>Cancelar</Button>
            <Button variant="primary" onClick={confirmMarcarAberto} disabled={actionLoading?.status === "ABERTO"}>
              {actionLoading?.status === "ABERTO" ? "Atualizando..." : "Confirmar"}
            </Button>
          </>
        }
      >
        <p>O horário desta cirurgia será alterado para <strong>23:59</strong> ao marcá-la como Aberto. Deseja continuar?</p>
      </Modal>

      <CirurgiaDetailsModal
        open={detalheAberto}
        onClose={() => { setDetalheAberto(false); setModoEdicao(false); }}
        cirurgia={cirurgiaSelecionada}
        modoEdicao={modoEdicao}
        onSalvar={handleSalvarEdicao}
      />

      <Drawer
        open={novaCirurgiaAberta}
        onClose={() => setNovaCirurgiaAberta(false)}
        title="Nova cirurgia"
        subtitle="Preencha os dados do procedimento — campos com * são obrigatórios."
        wide
        footer={
          <>
            <Button variant="outline" onClick={() => setNovaCirurgiaAberta(false)}>Cancelar</Button>
            <Button type="submit" form="nova-cirurgia-form" variant="primary" disabled={novaCirurgiaEnviando}>
              {novaCirurgiaEnviando ? "Agendando..." : "Agendar cirurgia"}
            </Button>
          </>
        }
      >
        <NovaCirurgiaForm
          onSuccess={() => { setNovaCirurgiaAberta(false); fetchCirurgias(); fetchContagens(); }}
          onSubmittingChange={setNovaCirurgiaEnviando}
        />
      </Drawer>

      <ConfirmDeleteDialog
        open={cirurgiaParaExcluir !== null}
        loading={excluindo}
        title="Excluir cirurgia definitivamente"
        confirmLabel="Excluir definitivamente"
        message={
          cirurgiaParaExcluir
            ? `A cirurgia de ${cirurgiaParaExcluir.paciente} em ${formatarDataCirurgia(cirurgiaParaExcluir.data)} será apagada do banco de dados. ` +
              "Isto não é um cancelamento: o registro deixa de existir, some dos relatórios e não há como recuperar. " +
              "Para manter o histórico, use \"Cancelar cirurgia\"."
            : ""
        }
        onCancel={() => setCirurgiaParaExcluir(null)}
        onConfirm={confirmarExclusao}
      />
    </div>
  );
};

export default ListaCirurgias;
